import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Calendar, Info } from "lucide-react";
import { getActiveLeaveTypes } from "../../api/payroll/leaveTypePortalApi";
import { applyLeave } from "../../api/leaves/leavePortalApi";
import { Button, Input, Select } from "../ui";
import WidgetCard from "../widget/WidgetCard";
import { useToast } from "../../context/ToastContext";

export const ApplyLeaveWidget = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    attachmentUrl: "",
  });

  const toast = useToast();

  const basePath = isPortal ? "/staff-panel/leaves" : "/leaves";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLeaveTypesLoading(true);
        const res = await getActiveLeaveTypes();
        const list = Array.isArray(res) ? res : res?.data || [];
        const activeList = list.filter((t) => t && t._id && t.active !== false);
        if (!cancelled) {
          setLeaveTypes(activeList);
          if (activeList.length > 0) {
            setFormData((prev) => ({ ...prev, leaveType: activeList[0]._id }));
          }
        }
      } catch (err) {
        if (!cancelled) toast.error("Failed to load leave types");
      } finally {
        if (!cancelled) setLeaveTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const leaveTypeId = String(formData.leaveType || "").trim();
    if (!leaveTypeId || leaveTypeId.length !== 24) return;

    setSubmitting(true);
    try {
      await applyLeave({
        leaveType: leaveTypeId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason.trim(),
        attachmentUrl: formData.attachmentUrl || "",
      });
      toast.success("Leave application submitted successfully!");
      setFormData({
        leaveType: leaveTypes[0]?._id || "",
        startDate: "",
        endDate: "",
        reason: "",
        attachmentUrl: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to apply for leave");
    } finally {
      setSubmitting(false);
    }
  };

  const leaveOptions = [
    { label: leaveTypesLoading ? "Loading..." : "Select leave type", value: "" },
    ...leaveTypes.map((t) => ({
      label: `${t.name} (${t.code})${t.isPaid === false ? " — Unpaid" : ""}`,
      value: t._id,
    })),
  ];

  return (
    <WidgetCard
      title="Apply for Leave"
      icon={Calendar}
      onViewAll={() => navigate(basePath)}
    >
      
      

      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Leave Type"
          name="leaveType"
          options={leaveOptions}
          value={formData.leaveType}
          onChange={(val) => setFormData((prev) => ({ ...prev, leaveType: val }))}
          disabled={leaveTypesLoading || leaveTypes.length === 0}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            type="date"
            label="Start Date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            required
          />

          <Input
            type="date"
            label="End Date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-neutral-200 uppercase tracking-wider mb-1">
            Reason for Leave
          </label>
          <textarea
            name="reason"
            rows={2}
            value={formData.reason}
            onChange={handleChange}
            placeholder="State reason clearly..."
            className="w-full rounded-xl border border-slate-200 dark:border-neutral-800 bg-slate-50/50 p-3 text-sm text-slate-800 dark:text-neutral-100 placeholder-slate-400 dark:placeholder-neutral-500 transition-all focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            required
          />
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-neutral-400 font-medium italic">
            <Info className="h-3 w-3 text-indigo-500 shrink-0" />
            Sundays & holidays are excluded automatically
          </p>

          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={submitting || leaveTypes.length === 0}
            leftIcon={<Send className="h-3.5 w-3.5" />}
            className="w-full sm:w-auto shadow-xs"
          >
            {submitting ? "Submitting..." : "Submit Application"}
          </Button>
        </div>
      </form>
    </WidgetCard>
  );
};